import { useEffect, useRef, useState } from "react";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const SCRIPT_ID = "google-maps-js";

let loaderPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google);
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      const check = () => (window.google?.maps ? resolve(window.google) : setTimeout(check, 50));
      check();
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&v=weekly`;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error("Google Maps JS failed to load"));
    document.head.appendChild(s);
  });
  return loaderPromise;
}

function pinColor(score) {
  if (score >= 75) return "#16A34A"; // green
  if (score >= 55) return "#CA8A04"; // yellow
  return "#DC2626"; // red
}

export default function MapView({ properties, onOpenProperty }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstances = useRef([]);
  const infoWindow = useRef(null);
  const [error, setError] = useState("");
  const [mapReady, setMapReady] = useState(false);

  // One-time map init
  useEffect(() => {
    let cancelled = false;
    if (!GOOGLE_KEY) {
      setError("Google Maps key is not configured.");
      return;
    }
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapRef.current) return;
        const center = firstValidLatLng(properties) || { lat: 39.8283, lng: -98.5795 };
        mapInstance.current = new google.maps.Map(mapRef.current, {
          center,
          zoom: 11,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          styles: lightStyle,
        });
        infoWindow.current = new google.maps.InfoWindow();
        setMapReady(true);
      })
      .catch((e) => setError(e.message || "Maps load failed"));
    return () => {
      cancelled = true;
    };
    // init once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh markers whenever properties change
  useEffect(() => {
    if (!mapReady || !mapInstance.current) return;
    const google = window.google;
    // Clear old
    markerInstances.current.forEach((m) => m.setMap(null));
    markerInstances.current = [];

    const bounds = new google.maps.LatLngBounds();
    let added = 0;

    properties.forEach((p) => {
      if (typeof p.latitude !== "number" || typeof p.longitude !== "number") return;
      const position = { lat: p.latitude, lng: p.longitude };
      const marker = new google.maps.Marker({
        position,
        map: mapInstance.current,
        title: p.address,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: pinColor(p.motivation_score),
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
          scale: 10,
        },
        label: {
          text: String(p.motivation_score || ""),
          color: "#fff",
          fontSize: "10px",
          fontWeight: "600",
        },
      });

      marker.addListener("click", () => {
        const arv = (p.estimated_arv || 0).toLocaleString();
        const ask = (p.asking_price || 0).toLocaleString();
        const mao = (p.mao_70 || 0).toLocaleString();
        const signals = (p.distress_signals || []).slice(0, 3).join(" · ") || "—";
        const html = `
          <div style="font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;min-width:240px;max-width:280px;">
            <div style="font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#A1A1AA;font-weight:600;">
              Motivation ${p.motivation_score}/100
            </div>
            <div style="font-weight:700;margin-top:4px;font-size:14px;">${escapeHtml(p.address)}</div>
            <div style="font-family:'IBM Plex Mono',monospace;color:#52525B;font-size:11px;margin-top:2px;">
              ${escapeHtml(p.city)}, ${escapeHtml(p.state)}
            </div>
            <div style="margin-top:10px;font-size:12px;line-height:1.55;">
              <div style="display:flex;justify-content:space-between;"><span style="color:#71717A;">Asking</span><span style="font-family:monospace;">$${ask}</span></div>
              <div style="display:flex;justify-content:space-between;"><span style="color:#71717A;">ARV</span><span style="font-family:monospace;">$${arv}</span></div>
              <div style="display:flex;justify-content:space-between;"><span style="color:#71717A;">MAO 70%</span><span style="font-family:monospace;font-weight:700;color:#002FA7;">$${mao}</span></div>
              <div style="display:flex;justify-content:space-between;margin-top:4px;"><span style="color:#71717A;">Signals</span><span style="font-family:monospace;font-size:10px;">${escapeHtml(signals)}</span></div>
            </div>
            <button id="open-prop-${p.id}" style="margin-top:12px;width:100%;background:#0A0A0A;color:#fff;border:0;padding:8px 12px;font-size:12px;font-weight:500;cursor:pointer;">
              View deal →
            </button>
          </div>`;
        infoWindow.current.setContent(html);
        infoWindow.current.open({ map: mapInstance.current, anchor: marker });
        setTimeout(() => {
          const btn = document.getElementById(`open-prop-${p.id}`);
          if (btn) {
            btn.onclick = () => {
              infoWindow.current.close();
              onOpenProperty(p);
            };
          }
        }, 0);
      });

      markerInstances.current.push(marker);
      bounds.extend(position);
      added += 1;
    });

    if (added > 0) {
      mapInstance.current.fitBounds(bounds);
      if (added === 1) mapInstance.current.setZoom(14);
    }
  }, [properties, mapReady, onOpenProperty]);

  if (error) {
    return (
      <div
        data-testid="map-error"
        className="border border-[--line] p-6 text-sm text-[--ink-muted] text-center"
      >
        Map unavailable — {error}
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      data-testid="property-map"
      className="w-full h-[620px] border border-[--line] bg-[--bg-soft]"
    />
  );
}

function firstValidLatLng(props) {
  for (const p of props || []) {
    if (typeof p.latitude === "number" && typeof p.longitude === "number") {
      return { lat: p.latitude, lng: p.longitude };
    }
  }
  return null;
}

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Soft monochrome style so markers pop
const lightStyle = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9d6df" }] },
];
